const PENDING_NATIVE_RESULT = "pending-native-result";
const AWAITING_BROWSER_RESULT = "awaiting-browser-result";
const AWAITING_BROWSER_AFTER_NATIVE_GRANT = "awaiting-browser-after-native-grant";
const IDLE = "idle";

/**
 * Coordinates one TWA location request across MapLibre's browser watch and the Android dialog.
 * Chrome can reject the original watch before Android has completed its visible dialog, so a
 * code-1 error is provisional until the native request reaches a terminal state.
 */
export function createNativeLocationPermissionRequest() {
  let state = IDLE;
  let interimErrorSeen = false;

  const reset = () => {
    state = IDLE;
    interimErrorSeen = false;
  };

  return {
    begin() {
      state = PENDING_NATIVE_RESULT;
      interimErrorSeen = false;
    },

    /** The map's session-key timer expired before native supplied a result. */
    markNativeResultUnavailable() {
      if (state === PENDING_NATIVE_RESULT) {
        state = AWAITING_BROWSER_RESULT;
      }
    },

    onMapLibreError(code) {
      if (Number(code) !== 1) {
        return { suppress: false };
      }

      if (state === PENDING_NATIVE_RESULT) {
        interimErrorSeen = true;
        return { suppress: true };
      }

      if (state === AWAITING_BROWSER_RESULT) {
        reset();
        return { suppress: true };
      }

      if (state === AWAITING_BROWSER_AFTER_NATIVE_GRANT) {
        return { suppress: true, browserBlocked: true };
      }

      return { suppress: false };
    },

    onNativeResult(result) {
      const wasPending = state === PENDING_NATIVE_RESULT;
      if (result === "denied") {
        reset();
        return { accepted: wasPending, denied: wasPending, retry: false };
      }

      if (result === "granted") {
        const retry = interimErrorSeen;
        state = AWAITING_BROWSER_AFTER_NATIVE_GRANT;
        interimErrorSeen = false;
        return { accepted: wasPending, denied: false, retry };
      }

      if (result === "abandoned" || result === "timeout") {
        state = AWAITING_BROWSER_RESULT;
        interimErrorSeen = false;
      }
      return { accepted: wasPending, denied: false, retry: false };
    },

    onMapLibreSuccess() {
      reset();
    },

    shouldRetryBrowserGeolocation() {
      return state === AWAITING_BROWSER_AFTER_NATIVE_GRANT;
    },

    getState() {
      return state;
    },
  };
}
